import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Package, Loader } from 'lucide-react';
import axios from 'axios';
import { API, useAuth } from '../App';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/dashboard');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPaymentData(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('failed');
      } else {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full mx-auto px-6"
      >
        <div className="bg-white border border-border p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader size={64} className="mx-auto mb-6 text-primary animate-spin" />
              <h2 className="text-2xl font-heading font-bold mb-2">Checking Payment Status...</h2>
              <p className="text-muted-foreground">Please wait while we confirm your payment</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle size={64} className="mx-auto mb-6 text-green-600" />
              <h2 className="text-3xl font-heading font-bold mb-2" data-testid="payment-success-title">Payment Successful!</h2>
              <p className="text-muted-foreground mb-6">Thank you for your order. Your payment has been processed successfully.</p>
              {paymentData && (
                <div className="bg-accent p-4 mb-6 text-left">
                  <p className="text-sm mb-1">
                    <span className="font-semibold">Amount Paid:</span> &#8377;{paymentData.amount.toFixed(2)}
                  </p>
                  <p className="text-sm">
                    <span className="font-semibold">Status:</span> {paymentData.payment_status}
                  </p>
                </div>
              )}
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-3 font-medium transition-all duration-300 mb-3"
                data-testid="view-orders-button"
              >
                <Package size={20} className="inline mr-2" />
                View My Orders
              </button>
              <button
                onClick={() => navigate('/products')}
                className="w-full border-2 border-border hover:border-primary text-foreground py-3 font-medium transition-all duration-300"
                data-testid="continue-shopping-button"
              >
                Continue Shopping
              </button>
            </>
          )}

          {status === 'failed' && (
            <>
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-destructive text-3xl">✕</span>
              </div>
              <h2 className="text-3xl font-heading font-bold mb-2">Payment Failed</h2>
              <p className="text-muted-foreground mb-6">Your payment could not be processed. Please try again.</p>
              <button
                onClick={() => navigate('/checkout')}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-3 font-medium transition-all duration-300"
              >
                Try Again
              </button>
            </>
          )}

          {(status === 'timeout' || status === 'error') && (
            <>
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-yellow-600 text-3xl">!</span>
              </div>
              <h2 className="text-3xl font-heading font-bold mb-2">Payment Status Pending</h2>
              <p className="text-muted-foreground mb-6">
                We're still processing your payment. Please check your email for confirmation or contact support.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-3 font-medium transition-all duration-300"
              >
                Go to Dashboard
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentSuccessPage;
